import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, Branding, useBCMSContext, useModel, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import DateSelect from 'components/custom-ui/DateSelect';
import UIRadioGroup, { UIRadio } from 'components/custom-ui/UIRadioGroup';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import WirePayeeConfirmationWidget from "components/shared/widgets/WirePayeeConfirmationWidget";
import { PrimaryButton, SecondaryButton, UIAmountInput, UICheckbox, UITextInput } from 'components/ui';
import Classes, { Account, Company, OneTimeWireTransfer, Payee, WireActivity } from "model";
import React from 'react';
import "./mobile.css";
import "./styles.css";


/**
 * @function SingleWirePage
 * @return {React.Component}
 * @exported true
 * @description The single wire page
 */

export default function SingleWirePage(props) {
  const { user, branding } = useModel();
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onOverrideOriginator: value => {
        const isActive = state.ui.overrideOriginator.isActive;
        const company = Company.firstInstanceOf();

        BCMSStateManagerCRUD.init(state)
          .update([ "ui", "overrideOriginator", "isActive" ], !isActive)
          .update([ "data", "overrideOriginator" ], !isActive ? company.name : "")
          .submit(setState);
      },

      onChange: prop => value => {
        let crud = BCMSStateManagerCRUD.init(state)

        if (prop === "futureWireDate") {
          crud.update(["data", "recurringWireDate"], undefined)
          crud.update(["data", "wire", "date"], value)
        }

        if (prop === "recurringWireDate") {
          crud.update(["data", "futureWireDate"], undefined)
          crud.update(["data", "wire", "date"], value)
        }

        crud.update(["data", prop], value)
        crud.submit(setState);
      },
      
      onWireChange: prop => value => {
        let crud = BCMSStateManagerCRUD.init(state);

        if (prop === "schedulingOption") {
          crud.update(["data", "futureWireDate"], undefined)
          crud.update(["data", "recurringWireDate"], undefined)
          crud.update(["data", "wire", "date"], undefined)
        }
        crud.update(["data", "wire", prop], value)
          .submit(setState);
      },
    onSubmitClick: () => {
      // Wire history data.
      let wireHistoryDefault = BCMSDefaultModel.wireHistory();
      let wireData = state.data.wire;

       // One time wire data.
      const defaultOneTimeWireTransfer = BCMSDefaultModel.oneTimeWireTransfer();
      defaultOneTimeWireTransfer.fundingAccount = Account.getDefaultBrandedAccountByType("checking").id;
      defaultOneTimeWireTransfer.initiatedBy = `${user.first} ${user.last}`;
      const combinedWires = [...OneTimeWireTransfer.initAll(), ...WireActivity.initAll()];
      
      // START: Increment the reference number for one time wire.
      const allOneTimeWireTransfers = combinedWires
        .filter(item => item.id !== undefined)
        .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
      let oneTimeWireRefNumber = defaultOneTimeWireTransfer.referenceNumber;
      if (allOneTimeWireTransfers.length > 0) {
        oneTimeWireRefNumber = parseFloat(allOneTimeWireTransfers[0].referenceNumber) + 1;
      }
      // END: Increment the reference number for one time wire.

      // START: Increment the reference number for single wire.
      const allWireHistory = combinedWires
        .filter(item => item.id !== undefined)
        .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
        let singleWireRefNumber = defaultOneTimeWireTransfer.referenceNumber;

      if (allWireHistory.length > 0) {
        singleWireRefNumber = parseInt(allWireHistory[0].referenceNumber, 10) + 1;
      
      }
      // END: Increment the reference number for single wire.
  
      // set values for history and one time wire transfer
      let wireTransfer = {
        ...wireData.record,
        date: wireData.date,
        fundingAccount: wireData?.fundingAccount ?? defaultOneTimeWireTransfer.fundingAccount,
        amount: wireData.amount.amount !== "" ? wireData.amount : defaultOneTimeWireTransfer.amount,
        initiatedBy:  defaultOneTimeWireTransfer.name,
        name: wireData.payee?.name ?? Payee.initByID(0).name,
        payeeName: wireData.payee?.name ?? Payee.initByID(0).name,
        wireTime: wireData.date ? BCMSDate.init(wireData.date).date.getTime() : 0
      };

      // If true, creates a new one time wire transfer or else a history wire.
      if (branding.options.wire_approvals_abi22 === "true") {

        wireTransfer = {
          ...wireTransfer,
          id: OneTimeWireTransfer.nextID(),
          referenceNumber: oneTimeWireRefNumber,
        };

        const oneTimeWireTransfer = OneTimeWireTransfer.init(wireTransfer);
        const mergeRecord = BCMSDefaultModel.mergeRecords(oneTimeWireTransfer, defaultOneTimeWireTransfer);
        BCMSRecord.batch([OneTimeWireTransfer.create({ ...mergeRecord })], globalController, Classes);

      } else {

        wireTransfer = {
          ...wireTransfer,
          id: WireActivity.nextID(),
          referenceNumber: singleWireRefNumber,
          payee: wireData.payee ?? Payee.initByID(0),
          statusDescription: defaultOneTimeWireTransfer.status,
        };

        const wireHistoryTransfer = WireActivity.init(wireTransfer);
        BCMSRecord.batch([WireActivity.create({ ...wireHistoryTransfer.record })], globalController, Classes);
      }

      BCMSStateManagerCRUD.init(state)
        .update(["data", "createdOneTimeWireTransfer"], wireTransfer)
        .update(["data", "wireTransferSubmitted"], true)
        .update(["data", "wire"], wireHistoryDefault)
        .update(["data", "didSetDate"], false)

        .update(["data", "overrideOriginator"], "")
        .update(["ui", "overrideOriginator", "isActive"], false)
        .submit(setState);

      window.scrollTo(0, 0);
    },

    onSubmittedConfirmationClose: () => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "createdOneTimeWireTransfer"], undefined)
        .update(["data", "wireTransferSubmitted"], false)
        .submit(setState);
    },

  });

  return(
    <PageContainer className="single-wire-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => {
  const defaultWire = BCMSDefaultModel.wireHistory();
  
  return ({
    futureWireDate: undefined, // wire property since undefined cant be set in the wire instance.
    wire: defaultWire,
    linkedAccounts: Account.initAllAsset(),
    payees: Payee.initAll(),
    recurringEndByDate: undefined, // wire property since undefined cant be set in the wire instance.
    recurringWireDate: undefined, // wire property since undefined cant be set in the wire instance.
    wireTransferSubmitted: false,
    createdOneTimeWireTransfer: undefined,
    overrideOriginator: ""
    /*amount: undefined,
    futureWireDate: undefined,
    linkedAccounts: LinkedAccount.initAll(),
    memo: undefined,
    payees: Payee.initAll(),
    recurringEndByDate: undefined,
    recurringWireDate: undefined,
    recurringNumberOfTransfers: undefined,
    selectedLinkedAccountID: "fundingAccount",
    selectedPayeeID: "selectPayee",
    selectedSchedulingOptionID: "selectSchedule",
    selectedFrequencyID: "monthly",
    selectedFrequencyTypeID: "noEndDate"*/
  });
}

export const getDefaultUI = () => ({
  payeeSelectIsActive: { isActive: false },
  fundingAccountSelectIsActive: { isActive: false },
  scheduleOptionSelectIsActive: { isActive: false },
  overrideOriginator: { isActive: false }
});

function PageContent(props) {
  const { data, ui, onOverrideOriginator, onChange, onWireChange, onSubmitClick,onSubmittedConfirmationClose } = useStateManager();
  const action = {
    onSubmittedConfirmationClose
};
  const navigate = useRoute();

  const payeeItems = data.payees.reduce((acc, payee, index) => {
    if(index === 0) {
      acc.push(<CustomSelectItem key="selectPayee" value="selectPayee" dataPoint={`Payee_Select_Placeholder`}>-- Select Payee --</CustomSelectItem>);
    }
    acc.push(<CustomSelectItem key={ payee.id } value={ payee.id } dataPoint={`Payee_Select_Item_${payee.id}`}>{ payee.nickname }</CustomSelectItem>);
    return acc;
  }, []);

  const linkedAccountItems = data.linkedAccounts.reduce((acc, account, index) => {
    let label = ``;

    if(account.type === "loan") {
      label = `${account.number} - ${account.nickname} (Current ${ account.currentBalance.format() })`;
    } else if(account.type === "loc") {
      label = `${account.number} - ${account.nickname} (Available ${ account.availableCredit.format() })`;
    } else {
      label = `${account.number} - ${account.nickname} (Available ${ account.availableBalance.format() })`;
    }
    
    if(index === 0 && acc.length === 0) {
      acc.push(<CustomSelectItem key={ "fundingAccount" } value="fundingAccount" dataPoint={`Funding_Account_Select_Placeholder`}>-- Select a Funding Account --</CustomSelectItem>);
      acc.push(<CustomSelectItem key={ account.id } value={ account.id } dataPoint={`Funding_Account_Select_Item_${account.id}`}>{ label }</CustomSelectItem>);
    } else {
      acc.push(<CustomSelectItem key={ account.id } value={ account.id } dataPoint={`Funding_Account_Select_Item_${account.id}`}>{ label }</CustomSelectItem>);
    }
    
    return acc;
  }, []);
  
  const scheduleOptions = [
    {id: "selectSchedule", name:"-- Select an Option --" },
    {id: "future", name:"Future (One Time)" },
    {id: "recurring", name:"Recurring" }
  ];
  const scheduleOptionItems = scheduleOptions.reduce((acc, option) => {
    acc.push(<CustomSelectItem key={ option.id } value={ option.id } dataPoint={`Schedule_Option_Select_Item_${option.id}`}>{ option.name }</CustomSelectItem>);
    return acc;
  }, []);

  const cancel = () => navigate("/wiresActivity");

  const isPayeeSelected = data.wire.payee ? true : false;
  const company = Company.firstInstanceOf();

  return (
  <PageContentView>
    { data.wireTransferSubmitted && 
      <WirePayeeConfirmationWidget data={ data } action={ action }  />
    }
    <CenterContentContainer>
      <ContentHeaderView text="Single Wire" dataPoint="Single_Wire_Title" />
      <FormContainer className="small-form-width">
        <FormRowView text="* Payee">
          <CustomSelect value={ data.wire.payee ? data.wire.payee.id : "selectPayee" } onChange={ onWireChange("payee") } dataPoint={`Payee_Select`} active={ui.payeeSelectIsActive.isActive}>
            { payeeItems }
          </CustomSelect>
        </FormRowView>

        <FormRowView text="Notify Payee" dataPoint="Single_Wire_Notify_Payee">
          { isPayeeSelected && <p className="notify-payee-value"><b>No</b></p> }
        </FormRowView>

        <FormRowView text="* Funding Account">
          <CustomSelect value={ data.wire.fundingAccount ? data.wire.fundingAccount.id : "fundingAccount" } onChange={ onWireChange("fundingAccount") } dataPoint={`Funding_Account_Select`} active={ui.fundingAccountSelectIsActive.isActive}>
            { linkedAccountItems }
          </CustomSelect>
        </FormRowView>

        <FormRowView>
          <UICheckbox label={ `Override Originator Name : ${ company.name }` } onChange={ onOverrideOriginator } value={ ui.overrideOriginator.isActive } dataPoint="Single_Wire_Override_Originator" />
        </FormRowView>

        {
          ui.overrideOriginator.isActive &&
          <FormRowView text="* Override Originator">
            <UITextInput value={ data.overrideOriginator } onChange={ onChange("overrideOriginator") } />
          </FormRowView>
        }

        <FormRowView text="* Amount">
          <UIAmountInput value={ data.wire.amount ? data.wire.amount.amount : undefined } onChange={ onWireChange("amount") } dataPoint={`Wire_Amount`}/>
        </FormRowView>
        <FormRowView text="* Scheduling Option">
          <CustomSelect value={ data.wire.schedulingOption ? data.wire.schedulingOption : "selectSchedule" } onChange={ onWireChange("schedulingOption") } dataPoint={`Scheduling_Option_Select`} active={ui.scheduleOptionSelectIsActive.isActive}>
            { scheduleOptionItems }
          </CustomSelect>
        </FormRowView>
        { data.wire.schedulingOption === "future" && 
        <div className="recurring-options-container">
          <FormRowView text="* Wire Date">
            <DateSelect value={ data.futureWireDate } onChange={ onChange("futureWireDate") } dataPoint="Future_Wire_Date"/>
          </FormRowView>
          </div>
        }
        { data.wire.schedulingOption === "recurring" && 
        <div className="recurring-options-container">
          <FormRowView text="* Wire Date">
            <DateSelect value={ data.recurringWireDate } onChange={ onChange("recurringWireDate") } dataPoint="Recurring_Wire_Date"/>
          </FormRowView>
          <FormRowView text="* Frequency">
            <CustomSelect value={ data.wire.frequency }>
              <CustomSelectItem value="daily">Daily</CustomSelectItem>
              <CustomSelectItem value="weekly">Weekly</CustomSelectItem>
              <CustomSelectItem value="everyTwoWeeks">Every two weeks</CustomSelectItem>
              <CustomSelectItem value="twiceAMonth">Twice a month</CustomSelectItem>
              <CustomSelectItem value="everyFourWeeks">Every four weeks</CustomSelectItem>
              <CustomSelectItem value="monthly">Monthly</CustomSelectItem>
              <CustomSelectItem value="everyTwoMonths">Every two months</CustomSelectItem>
              <CustomSelectItem value="quarterly">Quarterly</CustomSelectItem>
              <CustomSelectItem value="everySixMonths">Every six months</CustomSelectItem>
              <CustomSelectItem value="annually">Annually</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <UIRadioGroup value={ data.wire.frequencyType } onChange={ onWireChange("frequencyType") }>
            <FormRowView className="no-end-date-radio">
              <UIRadio name="frequency" value="noEndDate" label="No End Date" />
            </FormRowView>
            <FormRowView>
              <UIRadio name="frequency" value="endBy" label="End By:" />
              <DateSelect value={ data.recurringEndByDate } onChange={ onChange("recurringEndByDate") }/>
            </FormRowView>
            <FormRowView>
              <UIRadio name="frequency" value="numberOfTransfers" label="Number of Transfers:" />
              <UITextInput value={ data.wire.recurringNumberOfTransfers } onChange={ onWireChange("recurringNumberOfTransfers") } />
            </FormRowView>
          </UIRadioGroup>
        </div>
        }
        { isPayeeSelected &&
        <Branding id="reasonforpayment_abi22" value="true">
          <FormRowView text="Reason for Payment">
            <UITextInput value={data.wire.reasonForPayment} onChange={onWireChange("reasonForPayment")} dataPoint="Wire_Reason_For_Payment_Input" />
          </FormRowView>
        </Branding>
        }
        <Branding id="memo_abi22" value="true">
          <FormRowView text="Memo">
            <UITextInput value={ data.wire.memo } onChange={ onWireChange("memo") } dataPoint="Wire_Memo_Input" />
          </FormRowView>
        </Branding>

        <Branding id="beneficiaryref_abi22" value="true">
          <FormRowView text="Beneficiary Reference">
            <UITextInput value={ data.wire.beneficiaryReference } onChange={ onWireChange("beneficiaryReference") } dataPoint="Wire_Beneficiary_Reference_Input" />
          </FormRowView>
        </Branding>

        { isPayeeSelected &&
        <Branding id="messagetobeneficiary_abi22" value="true">
          <FormRowView className="message-to-beneficiary" text="Message to Beneficiary">
            <UITextInput value={ data.wire.messageToBeneficiary1 } onChange={ onWireChange("messageToBeneficiary1") } dataPoint="Wire_Message_To_Beneficiary_Input_0" />
          </FormRowView>
          <FormRowView className="message-to-beneficiary">
            <UITextInput value={ data.wire.messageToBeneficiary2 } onChange={ onWireChange("messageToBeneficiary2") } dataPoint="Wire_Message_To_Beneficiary_Input_1" />
          </FormRowView>
          <FormRowView className="message-to-beneficiary">
            <UITextInput value={ data.wire.messageToBeneficiary3 } onChange={ onWireChange("messageToBeneficiary3") } dataPoint="Wire_Message_To_Beneficiary_Input_2" />
          </FormRowView>
          <FormRowView className="message-to-beneficiary">
            <UITextInput value={ data.wire.messageToBeneficiary4 } onChange={ onWireChange("messageToBeneficiary4") } dataPoint="Wire_Message_To_Beneficiary_Input_3" />
          </FormRowView>
        </Branding>
        }
        
        <FormButtonRow>
          <SecondaryButton text="Cancel" onClick={ cancel } />

            <PrimaryButton text="Submit" dataPoint="Submit_Button"  onClick={onSubmitClick}/>
  
        </FormButtonRow>
        
      </FormContainer>
    </CenterContentContainer>
  </PageContentView>
  );
}